/**
 * Aba Gasto da conta: gasto por dia (barras por conta) + venda atribuída (linha), e o bloco mês contra mês
 * (pedido dela, 09/10/2026: "indicador de gasto da conta com filtros de 7/15/30 dias e mês contra mês").
 */
import { useMemo } from 'react'
import { Alert, Box, Card, CardContent, Grid, Stack, Typography, useTheme } from '@mui/material'
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { KpiCard } from '../../components/KpiCard'
import { brl, inteiro, nomeConta, pct, UIDS, type GastoDiario, type Kpis } from './logic'
import { Vazio } from './ui'

const CORES: Record<string, string> = { '777058595': '#3b82f6', '3131364517': '#f59e0b' }

function ddmm(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`
}

type BlocoMM = { atual: Kpis; anterior: Kpis; delta: Partial<Record<keyof Kpis, number | null>> }

export function AbaGasto({ gasto, contas }: { gasto: GastoDiario; contas: string[] }) {
  const theme = useTheme()
  // Tudo memoizado: o Recharts 3 reage à identidade de `data`; um array novo a cada render entra em loop
  // ("Maximum update depth exceeded") — visto na prévia de 09/10/2026.
  const uids = useMemo(() => (contas.length ? contas : UIDS), [contas])
  const serie = useMemo(
    () =>
      gasto.serie.map((p) => {
        const o: Record<string, string | number | null> = { dia: ddmm(p.data), venda: p.total.total_amount }
        for (const u of uids) o[u] = (p[u] as Kpis | undefined)?.cost ?? 0
        return o
      }),
    [gasto, uids],
  )
  const mm = gasto.mes_contra_mes
  const per = mm.periodos
  const blocos = useMemo(() => [...uids, 'total'].map((u) => [u, mm[u] as BlocoMM | undefined] as const).filter(([, b]) => b), [mm, uids])

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Typography sx={{ fontWeight: 700 }}>Gasto por dia</Typography>
          {/* legenda em texto: o <Legend> do Recharts 3 entra em loop de medição dentro do ResponsiveContainer (visto 09/10/2026) */}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            {uids.map((u) => `■ gasto ${nomeConta(u)}`).join(' · ')} · ╌ venda atribuída (eixo da direita)
          </Typography>
          <Box sx={{ width: '100%', height: { xs: 240, sm: 300 } }}>
            {serie.length ? (
              <ResponsiveContainer>
                <ComposedChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                  <XAxis dataKey="dia" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="g" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `R$${Math.round(Number(v))}`} />
                  <YAxis yAxisId="v" orientation="right" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => `R$${Math.round(Number(v))}`} />
                  <Tooltip formatter={(v, name) => [brl(Number(v)), name === 'venda' ? 'Venda atribuída' : `Gasto ${nomeConta(String(name))}`]} contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}` }} />
                  {uids.map((u) => <Bar key={u} yAxisId="g" dataKey={u} stackId="g" fill={CORES[u] || theme.palette.primary.main} />)}
                  <Line yAxisId="v" type="monotone" dataKey="venda" stroke={theme.palette.text.secondary} strokeWidth={1.75} strokeDasharray="5 4" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <Vazio texto="Sem gasto gravado neste período." />}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography sx={{ fontWeight: 700 }}>Mês contra mês</Typography>
          {per ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
              {ddmm(per.atual[0])} a {ddmm(per.atual[1])} contra {ddmm(per.anterior[0])} a {ddmm(per.anterior[1])} (dias fechados até D-1)
            </Typography>
          ) : null}
          {!mm.disponivel ? (
            <Alert severity="info">
              {mm.serie_desde
                ? `A série de Ads começa em ${ddmm(mm.serie_desde)}; o mês contra mês fica disponível quando houver o mês anterior inteiro na base.`
                : 'Sem série de Ads na base ainda.'}
            </Alert>
          ) : (
            <Stack spacing={2}>
              {blocos.map(([u, b]) => (
                <Box key={u}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{u === 'total' ? 'As duas contas' : nomeConta(u)}</Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}><KpiCard label="Gasto" value={brl(b!.atual.cost)} hint={`antes ${brl(b!.anterior.cost)}`} deltaPct={b!.delta.cost ?? null} deltaSufixo="vs mês ant." /></Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}><KpiCard label="Venda atribuída" value={brl(b!.atual.total_amount)} hint={`antes ${brl(b!.anterior.total_amount)}`} deltaPct={b!.delta.total_amount ?? null} deltaSufixo="vs mês ant." /></Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}><KpiCard label="ACOS" value={pct(b!.atual.acos)} hint={`antes ${pct(b!.anterior.acos)}`} deltaPct={b!.delta.acos ?? null} deltaSufixo="vs mês ant." /></Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}><KpiCard label="ROAS" value={b!.atual.roas == null ? '—' : `${b!.atual.roas}×`} hint={`antes ${b!.anterior.roas == null ? '—' : `${b!.anterior.roas}×`}`} deltaPct={b!.delta.roas ?? null} deltaSufixo="vs mês ant." /></Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}><KpiCard label="Cliques" value={inteiro(b!.atual.clicks)} hint={`antes ${inteiro(b!.anterior.clicks)}`} deltaPct={b!.delta.clicks ?? null} deltaSufixo="vs mês ant." /></Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}
